import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Box from "./BoxComponent";
import CustomButton from "./CustomButton";
import lootka from "../assets/images/newLootkaFont.webp";
import { ReactComponent as Close } from "../assets/icons/x-circle.svg";
import RegisterChoise from "./RegisterChoise";
import useUserStore from "../store/userStore";
import { AuthApiError, otpSignup, requestOtp } from "../services/authService";

interface SignUpProps {
  onClose?: () => void;
  switchToLogin?: () => void;
}

const SignUp = ({ onClose, switchToLogin }: SignUpProps) => {
  const [username, setUsername] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const setUser = useUserStore((state) => state.setUser);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setInterval(() => setResendSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  const showError = (error: unknown) => {
    if (error instanceof AuthApiError) {
      if (error.type === "mobile_in_use") {
        toast.error("این شماره قبلاً ثبت‌نام شده است. وارد شوید یا رمز را بازیابی کنید.", { className: "toast" });
        return;
      }
      if (error.type === "invalid_otp") {
        toast.error("کد واردشده نادرست است یا منقضی شده است.", { className: "toast" });
        return;
      }
      if (error.status === 429 || error.type === "otp_cooldown") {
        toast.error("لطفاً کمی صبر کنید و دوباره تلاش کنید.", { className: "toast" });
        return;
      }
      toast.error(error.message, { className: "toast" });
      return;
    }
    toast.error("خطایی رخ داد. لطفاً دوباره تلاش کنید.", { className: "toast" });
  };

  const requestCode = async () => {
    if (!username.trim() || !mobile.trim() || password.length < 8) {
      toast.error("نام کاربری، شماره موبایل و رمز حداقل ۸ کاراکتری را وارد کنید.", { className: "toast" });
      return;
    }
    try {
      setIsLoading(true);
      const result = await requestOtp(mobile, "signup");
      setCodeSent(true);
      setResendSeconds(result.data?.resend_after_seconds ?? 60);
      toast.success("کد تایید برای شماره شما ارسال شد.", { className: "toast" });
    } catch (error) {
      showError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const submitSignup = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!codeSent) {
      await requestCode();
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      toast.error("کد تایید باید دقیقاً ۶ رقم باشد.", { className: "toast" });
      return;
    }
    try {
      setIsLoading(true);
      const response = await otpSignup({
        username,
        mobile,
        password,
        code,
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(fullName.trim() ? { full_name: fullName.trim() } : {}),
      });
      const payload = response.data ?? response;
      const user = payload.user ?? payload;
      const token = payload.session_token ?? response.session_token ?? null;
      if (user?.user_id) {
        setUser({ userId: user.user_id, username: user.username, email: user.email, fullName: user.full_name, image: user.image ?? null }, typeof token === "string" ? token : null);
      }
      toast.success("ثبت‌نام با موفقیت انجام شد.", { className: "toast" });
      onClose?.();
    } catch (error) {
      showError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box heightClass="min-h-[560px] p-5 relative">
      <button onClick={onClose} className="absolute left-6 top-6 z-50 text-2xl text-gray-700 hover:text-gray-500 desktop:hidden" aria-label="بستن"><Close /></button>
      <img src={lootka} alt="لوتکا" className="absolute top-4 w-32 object-contain tablet:w-40" />
      <RegisterChoise switchToLogin={switchToLogin} active="signup" />

      <form onSubmit={submitSignup} className="mt-28 w-full max-w-[320px] space-y-3">
        <input value={username} onChange={(event) => setUsername(event.target.value)} disabled={codeSent} autoComplete="username" placeholder="نام کاربری" className="h-10 w-full rounded-xl border border-gray-300 px-3 text-right text-sm outline-none focus:border-[#038545] disabled:bg-gray-50" />
        <input value={mobile} onChange={(event) => setMobile(event.target.value)} disabled={codeSent} type="tel" inputMode="tel" autoComplete="tel" placeholder="شماره موبایل" className="h-10 w-full rounded-xl border border-gray-300 px-3 text-right text-sm outline-none focus:border-[#038545] disabled:bg-gray-50" />
        <input value={password} onChange={(event) => setPassword(event.target.value)} disabled={codeSent} type="password" autoComplete="new-password" placeholder="رمز عبور (حداقل ۸ کاراکتر)" className="h-10 w-full rounded-xl border border-gray-300 px-3 text-right text-sm outline-none focus:border-[#038545] disabled:bg-gray-50" />
        {!codeSent && <>
          <input value={fullName} onChange={(event) => setFullName(event.target.value)} autoComplete="name" placeholder="نام و نام خانوادگی (اختیاری)" className="h-10 w-full rounded-xl border border-gray-300 px-3 text-right text-sm outline-none focus:border-[#038545]" />
          <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" placeholder="ایمیل (اختیاری)" className="h-10 w-full rounded-xl border border-gray-300 px-3 text-right text-sm outline-none focus:border-[#038545]" />
        </>}
        {codeSent && <>
          <p className="text-xs leading-6 text-gray-600">کد تایید برای شماره واردشده ارسال شد.</p>
          <input value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="کد ۶ رقمی" className="h-11 w-full rounded-xl border border-gray-300 px-3 text-center text-lg tracking-[0.35em] outline-none focus:border-[#038545]" />
          <button type="button" disabled={resendSeconds > 0 || isLoading} onClick={() => void requestCode()} className="text-xs text-[#068646] disabled:text-gray-400">{resendSeconds > 0 ? `ارسال مجدد تا ${resendSeconds} ثانیه` : "ارسال مجدد کد"}</button>
        </>}
        <CustomButton type="submit" disabled={isLoading} text={isLoading ? "در حال بررسی..." : codeSent ? "تایید و ثبت‌نام" : "ارسال کد تایید"} className="mt-2 bg-[#038545] text-white w-full rounded-xl justify-center text-sm font-myYekanMedium disabled:opacity-50" />
      </form>
    </Box>
  );
};

export default SignUp;
